/**
 * Saudi Arabia HR attendance / timesheet helpers.
 * Work week: Sunday–Thursday; weekend: Friday–Saturday (Saudi Labor Law).
 */
import {
  formatBrandMoney,
  formatBrandMoneyCompact,
  getBrandLocale,
  getBrandDatePickerLocale,
} from '../../shared/utils/brandCurrency';
import { getRegionLabel } from '../../shared/utils/brandRegions';

export const POINTAGE_HELPERS_I18N_NS = 'modules.pointage.pages.pointageHelpers';

export {
  formatBrandMoney,
  formatBrandMoneyCompact,
  getBrandLocale,
  getBrandDatePickerLocale,
};

/** JS Date.getDay() indices for Saudi weekend (Friday=5, Saturday=6). */
export const SAUDI_WEEKEND_DAY_INDICES = [5, 6];

export const SAUDI_WEEKEND_DAYS = ['Friday', 'Saturday'];
export const SAUDI_WORK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'];

/** Stored day-of-week values (English). */
export const SAUDI_DAY_VALUES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

/** Five daily prayers — allowed break windows under Saudi labor practice. */
export const SAUDI_PRAYER_NAMES = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];

export const SAUDI_PRAYER_NAMES_AR = {
  Fajr: 'الفجر',
  Dhuhr: 'الظهر',
  Asr: 'العصر',
  Maghrib: 'المغرب',
  Isha: 'العشاء',
};

/** Saudi shift types — English DB values; Arabic labels via i18n. */
export const SHIFT_TYPE_VALUES = {
  day: 'Day',
  morning: 'Morning',
  evening: 'Evening',
  night: 'Night',
  rotating: 'Rotating',
  ramadan: 'Ramadan',
  weekend: 'Weekend',
};

export const LEGACY_SHIFT_TYPE_MAP = {
  Matin: SHIFT_TYPE_VALUES.morning,
  'Après-midi': SHIFT_TYPE_VALUES.evening,
  Nuit: SHIFT_TYPE_VALUES.night,
  'Week-end': SHIFT_TYPE_VALUES.weekend,
  Flexible: SHIFT_TYPE_VALUES.day,
};

export const SAUDI_SHIFT_PRESETS = {
  [SHIFT_TYPE_VALUES.day]: { start: '07:00', end: '15:00', breakMinutes: 60, dailyHours: 8 },
  [SHIFT_TYPE_VALUES.morning]: { start: '07:00', end: '15:00', breakMinutes: 60, dailyHours: 8 },
  [SHIFT_TYPE_VALUES.evening]: { start: '15:00', end: '23:00', breakMinutes: 60, dailyHours: 8 },
  [SHIFT_TYPE_VALUES.night]: { start: '23:00', end: '07:00', breakMinutes: 60, dailyHours: 8 },
  [SHIFT_TYPE_VALUES.rotating]: { start: '07:00', end: '15:00', breakMinutes: 60, dailyHours: 8 },
  [SHIFT_TYPE_VALUES.ramadan]: { start: '09:00', end: '15:00', breakMinutes: 30, dailyHours: 6 },
  [SHIFT_TYPE_VALUES.weekend]: { start: '08:00', end: '16:00', breakMinutes: 60, dailyHours: 8 },
};

export const LEAVE_TYPE_VALUES = {
  annual: 'Annual Leave',
  sick: 'Sick Leave',
  unpaid: 'Unpaid Leave',
  maternity: 'Maternity Leave',
  hajj: 'Hajj Leave',
  bereavement: 'Bereavement Leave',
  marriage: 'Marriage Leave',
  paternity: 'Paternity Leave',
};

export const LEGACY_LEAVE_TYPE_MAP = {
  'Congé Annuel': LEAVE_TYPE_VALUES.annual,
  'Congé Maladie': LEAVE_TYPE_VALUES.sick,
  'Congé Sans Solde': LEAVE_TYPE_VALUES.unpaid,
  'Congé Maternité': LEAVE_TYPE_VALUES.maternity,
  RTT: LEAVE_TYPE_VALUES.unpaid,
};

export const HOLIDAY_TYPE_VALUES = {
  national: 'National',
  religious: 'Religious',
  company: 'Company',
  regional: 'Regional',
};

export const LEGACY_HOLIDAY_TYPE_MAP = {
  Religieux: HOLIDAY_TYPE_VALUES.religious,
  Entreprise: HOLIDAY_TYPE_VALUES.company,
  Régional: HOLIDAY_TYPE_VALUES.regional,
};

export const HOLIDAY_CATEGORY_VALUES = {
  paid: 'Paid Holiday',
  unpaid: 'Unpaid Holiday',
  bridge: 'Bridge Day',
};

export const LEGACY_HOLIDAY_CATEGORY_MAP = {
  'Férié Payé': HOLIDAY_CATEGORY_VALUES.paid,
  'Férié Non Payé': HOLIDAY_CATEGORY_VALUES.unpaid,
  Pont: HOLIDAY_CATEGORY_VALUES.bridge,
};

export const ZONE_TYPE_VALUES = {
  office: 'Office',
  factory: 'Factory',
  warehouse: 'Warehouse',
  site: 'Site',
  branch: 'Branch',
};

export const LEGACY_ZONE_TYPE_MAP = {
  Bureau: ZONE_TYPE_VALUES.office,
  Usine: ZONE_TYPE_VALUES.factory,
  Entrepôt: ZONE_TYPE_VALUES.warehouse,
  Chantier: ZONE_TYPE_VALUES.site,
  Agence: ZONE_TYPE_VALUES.branch,
};

export const PROFILE_TYPE_VALUES = {
  standard: 'Standard',
  custom: 'Custom',
  fullTime: 'Full Time',
  partTime: 'Part Time',
  executive: 'Executive',
};

export const LEGACY_PROFILE_TYPE_MAP = {
  Personnalisé: PROFILE_TYPE_VALUES.custom,
  'Temps Plein': PROFILE_TYPE_VALUES.fullTime,
  'Temps Partiel': PROFILE_TYPE_VALUES.partTime,
  Cadre: PROFILE_TYPE_VALUES.executive,
};

export const FLEXIBILITY_TYPE_VALUES = {
  fixed: 'Fixed',
  variable: 'Variable Hours',
  remote: 'Remote',
  hybrid: 'Hybrid',
};

export const LEGACY_FLEXIBILITY_TYPE_MAP = {
  Fixe: FLEXIBILITY_TYPE_VALUES.fixed,
  'Horaire Variable': FLEXIBILITY_TYPE_VALUES.variable,
  Télétravail: FLEXIBILITY_TYPE_VALUES.remote,
  Hybride: FLEXIBILITY_TYPE_VALUES.hybrid,
};

export const ASSIGNMENT_STATUS_VALUES = {
  active: 'Active',
  suspended: 'Suspended',
  ended: 'Ended',
  planned: 'Planned',
};

export const LEGACY_ASSIGNMENT_STATUS_MAP = {
  Suspendue: ASSIGNMENT_STATUS_VALUES.suspended,
  Terminée: ASSIGNMENT_STATUS_VALUES.ended,
  Planifiée: ASSIGNMENT_STATUS_VALUES.planned,
};

export const ASSIGNMENT_TYPE_VALUES = {
  permanent: 'Permanent',
  temporary: 'Temporary',
  mission: 'Mission',
};

export const LEGACY_ASSIGNMENT_TYPE_MAP = {
  Permanente: ASSIGNMENT_TYPE_VALUES.permanent,
  Temporaire: ASSIGNMENT_TYPE_VALUES.temporary,
  Mission: ASSIGNMENT_TYPE_VALUES.mission,
};

export const TEAM_ROLE_VALUES = {
  teamLead: 'Team Lead',
  member: 'Member',
  substitute: 'Substitute',
  intern: 'Intern',
};

export const LEGACY_TEAM_ROLE_MAP = {
  "Chef d'Équipe": TEAM_ROLE_VALUES.teamLead,
  Membre: TEAM_ROLE_VALUES.member,
  Remplaçant: TEAM_ROLE_VALUES.substitute,
  Stagiaire: TEAM_ROLE_VALUES.intern,
};

export const JUSTIFICATION_TYPE_VALUES = {
  medical: 'Medical Certificate',
  death: 'Death Certificate',
  marriage: 'Marriage Certificate',
  emergency: 'Emergency Documentation',
  hajjTravel: 'Hajj Travel Documents',
  court: 'Court Summons',
  late: 'Late Justification',
  absence: 'Absence Authorization',
  other: 'Other',
};

export const LEGACY_JUSTIFICATION_TYPE_MAP = {
  'Certificat Médical': JUSTIFICATION_TYPE_VALUES.medical,
  Attestation: JUSTIFICATION_TYPE_VALUES.emergency,
  'Justificatif Retard': JUSTIFICATION_TYPE_VALUES.late,
  'Autorisation Absence': JUSTIFICATION_TYPE_VALUES.absence,
  Autre: JUSTIFICATION_TYPE_VALUES.other,
};

export const JUSTIFICATION_STATUS_VALUES = {
  pending: 'Pending',
  validated: 'Validated',
  rejected: 'Rejected',
};

export const LEGACY_JUSTIFICATION_STATUS_MAP = {
  'En Attente': JUSTIFICATION_STATUS_VALUES.pending,
  Validé: JUSTIFICATION_STATUS_VALUES.validated,
  Rejeté: JUSTIFICATION_STATUS_VALUES.rejected,
};

export const LEAVE_STATUS_VALUES = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const LEGACY_LEAVE_STATUS_MAP = {
  'En Attente': LEAVE_STATUS_VALUES.pending,
  Approuvée: LEAVE_STATUS_VALUES.approved,
  Rejetée: LEAVE_STATUS_VALUES.rejected,
};

export const SAUDI_WORK_CITIES = [
  { value: 'riyadh', label: 'الرياض', region: 'riyadh' },
  { value: 'jeddah', label: 'جدة', region: 'makkah' },
  { value: 'dammam', label: 'الدمام', region: 'eastern' },
  { value: 'makkah', label: 'مكة المكرمة', region: 'makkah' },
  { value: 'madinah', label: 'المدينة المنورة', region: 'madinah' },
  { value: 'khobar', label: 'الخبر', region: 'eastern' },
  { value: 'abha', label: 'أبها', region: 'asir' },
];

export const SAUDI_PUBLIC_HOLIDAY_TEMPLATES = [
  { key: 'foundation_day', nameKey: 'holiday_foundation_day', month: 2, day: 22, holiday_type: HOLIDAY_TYPE_VALUES.national, category: HOLIDAY_CATEGORY_VALUES.paid, is_recurring: true, calendar: 'gregorian' },
  { key: 'national_day', nameKey: 'holiday_national_day', month: 9, day: 23, holiday_type: HOLIDAY_TYPE_VALUES.national, category: HOLIDAY_CATEGORY_VALUES.paid, is_recurring: true, calendar: 'gregorian' },
  { key: 'eid_fitr', nameKey: 'holiday_eid_fitr', holiday_type: HOLIDAY_TYPE_VALUES.religious, category: HOLIDAY_CATEGORY_VALUES.paid, is_recurring: true, calendar: 'hijri', durationDays: 4 },
  { key: 'eid_adha', nameKey: 'holiday_eid_adha', holiday_type: HOLIDAY_TYPE_VALUES.religious, category: HOLIDAY_CATEGORY_VALUES.paid, is_recurring: true, calendar: 'hijri', durationDays: 5 },
  { key: 'islamic_new_year', nameKey: 'holiday_islamic_new_year', holiday_type: HOLIDAY_TYPE_VALUES.religious, category: HOLIDAY_CATEGORY_VALUES.paid, is_recurring: true, calendar: 'hijri', durationDays: 1 },
  { key: 'prophet_birthday', nameKey: 'holiday_prophet_birthday', holiday_type: HOLIDAY_TYPE_VALUES.religious, category: HOLIDAY_CATEGORY_VALUES.paid, is_recurring: true, calendar: 'hijri', durationDays: 1, optional: true },
];

export function getSaudiWorkCityOptions() {
  return SAUDI_WORK_CITIES.map((c) => ({ value: c.value, label: c.label }));
}

export function getSaudiWorkCityLabel(value) {
  if (!value) return '—';
  const city = SAUDI_WORK_CITIES.find((c) => c.value === value || c.label === value);
  return city?.label || getRegionLabel(value) || value;
}

export function normalizeShiftType(type) {
  if (!type) return SHIFT_TYPE_VALUES.morning;
  return LEGACY_SHIFT_TYPE_MAP[type] || type;
}

export function matchesShiftType(recordShift, filterShift) {
  return normalizeShiftType(recordShift) === normalizeShiftType(filterShift);
}

export function normalizeLeaveType(type) {
  if (!type) return type;
  return LEGACY_LEAVE_TYPE_MAP[type] || type;
}

export function normalizeHolidayType(type) {
  if (!type) return type;
  return LEGACY_HOLIDAY_TYPE_MAP[type] || type;
}

export function normalizeHolidayCategory(category) {
  if (!category) return category;
  return LEGACY_HOLIDAY_CATEGORY_MAP[category] || category;
}

export function normalizeZoneType(type) {
  if (!type) return type;
  return LEGACY_ZONE_TYPE_MAP[type] || type;
}

export function normalizeProfileType(type) {
  if (!type) return type;
  return LEGACY_PROFILE_TYPE_MAP[type] || type;
}

export function normalizeFlexibilityType(type) {
  if (!type) return type;
  return LEGACY_FLEXIBILITY_TYPE_MAP[type] || type;
}

export function normalizeAssignmentStatus(status) {
  if (!status) return status;
  return LEGACY_ASSIGNMENT_STATUS_MAP[status] || status;
}

export function normalizeAssignmentType(type) {
  if (!type) return type;
  return LEGACY_ASSIGNMENT_TYPE_MAP[type] || type;
}

export function normalizeTeamRole(role) {
  if (!role) return role;
  return LEGACY_TEAM_ROLE_MAP[role] || role;
}

export function normalizeJustificationType(type) {
  if (!type) return type;
  return LEGACY_JUSTIFICATION_TYPE_MAP[type] || type;
}

export function normalizeJustificationStatus(status) {
  if (!status) return JUSTIFICATION_STATUS_VALUES.pending;
  return LEGACY_JUSTIFICATION_STATUS_MAP[status] || status;
}

export function normalizeLeaveStatus(status) {
  if (!status) return LEAVE_STATUS_VALUES.pending;
  return LEGACY_LEAVE_STATUS_MAP[status] || status;
}

export function saudiFixedHolidayDate(year, month, day) {
  const y = Number(year) || new Date().getFullYear();
  return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Legacy French day names still present in older records. */
export const LEGACY_FRENCH_DAY_MAP = {
  Lundi: 'Monday',
  Mardi: 'Tuesday',
  Mercredi: 'Wednesday',
  Jeudi: 'Thursday',
  Vendredi: 'Friday',
  Samedi: 'Saturday',
  Dimanche: 'Sunday',
};

/** Saudi payroll — monthly salary ÷ 240 working hours. */
export const SAUDI_MONTHLY_HOURS_DIVISOR = 240;
export const SAUDI_MAX_ANNUAL_OVERTIME_HOURS = 180;
export const SAUDI_MAX_DAILY_OVERTIME_HOURS = 3;
export const SAUDI_OVERTIME_RATE = 1.5;
export const SAUDI_STANDARD_DAILY_HOURS = 8;
export const SAUDI_WEEKLY_REGULAR_HOURS = 48;
export const SAUDI_RAMADAN_DAILY_HOURS = 6;
export const SAUDI_RAMADAN_WEEKLY_HOURS = 36;
export const SAUDI_CURRENCY = 'SAR';

/** Fixed Gregorian public holidays (month is 1-based). */
export const SAUDI_FIXED_HOLIDAYS = [
  { month: 2, day: 22, key: 'foundation_day' },
  { month: 9, day: 23, key: 'national_day' },
];

/** Saudi Labor Law defaults for timetracking configuration. */
export const SAUDI_DEFAULT_TIMETRACKING_CONFIG = {
  workDayStart: '07:00',
  workDayEnd: '15:00',
  lunchBreakDuration: 60,
  lunchBreakStart: '12:00',
  weekendDays: SAUDI_WEEKEND_DAYS,
  workWeek: SAUDI_WORK_DAYS,
  /** Standard working day (8 h). */
  maxDailyHours: 8,
  /** Maximum daily hours including overtime (11 h). */
  maxDailyHoursWithOvertime: 11,
  maxWeeklyHours: 48,
  ramadanDailyHours: 6,
  ramadanWeeklyHours: 36,
  overtimeThreshold: 8,
  /** Regular overtime: 150% of base wage. */
  overtimeRate: 1.5,
  fridayHolidayOvertimeRate: 1.5,
  nightShiftRate: 1.5,
  maxAnnualOvertimeHours: 180,
  maxOvertimeYearly: 180,
  gracePeriodMinutes: 15,
  currency: SAUDI_CURRENCY,
  mandatoryBreakAfterHours: 5,
  prayerBreakMinutes: 30,
  autoApproveOvertime: false,
  maxAnnualLeave: 21,
  maxSickLeave: 120,
  maternityLeaveWeeks: 10,
  requireJustification: true,
  advanceNoticeRequired: 2,
  /** Grace period before lateness (minutes). */
  latenessThreshold: 15,
  maxLatenessPerMonth: 3,
  /** Max penalty for absence without notice (days' wages). */
  maxAbsencePenaltyDays: 5,
  autoGenerateWarnings: true,
  notifyOnAbsenceRequest: true,
  notifyOnOvertimeRequest: true,
  notifyOnLateness: true,
  emailNotifications: true,
  use24HourFormat: true,
  showWeekNumbers: false,
  defaultView: 'daily',
};

export function normalizeTimeTrackingConfig(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ...SAUDI_DEFAULT_TIMETRACKING_CONFIG };
  }
  return { ...SAUDI_DEFAULT_TIMETRACKING_CONFIG, ...raw };
}

const HIJRI_PREF_KEY = 'pointage_prefs';

/** Overtime type values persisted to API (English). */
export const OVERTIME_TYPE_VALUES = {
  regular: 'Regular',
  friday: 'Friday',
  holiday: 'Holiday',
  night: 'Night',
};

/** Map legacy French / Algerian stored values to current Saudi types. */
export const LEGACY_OVERTIME_TYPE_MAP = {
  Normal: OVERTIME_TYPE_VALUES.regular,
  Nuit: OVERTIME_TYPE_VALUES.night,
  Weekend: OVERTIME_TYPE_VALUES.friday,
  'Férié': OVERTIME_TYPE_VALUES.holiday,
  Ferie: OVERTIME_TYPE_VALUES.holiday,
};

/** Overtime pay multipliers per Saudi Labor Law (150% base). */
export const SAUDI_OVERTIME_MULTIPLIERS = {
  [OVERTIME_TYPE_VALUES.regular]: 1.5,
  [OVERTIME_TYPE_VALUES.night]: 1.5,
  [OVERTIME_TYPE_VALUES.friday]: 1.5,
  [OVERTIME_TYPE_VALUES.holiday]: 1.5,
};

export function normalizeOvertimeType(type) {
  if (!type) return OVERTIME_TYPE_VALUES.regular;
  return LEGACY_OVERTIME_TYPE_MAP[type] || type;
}

export function getOvertimeMultiplier(type) {
  const normalized = normalizeOvertimeType(type);
  return SAUDI_OVERTIME_MULTIPLIERS[normalized] ?? SAUDI_DEFAULT_TIMETRACKING_CONFIG.overtimeRate;
}

export function calculateOvertimeAmount(hours, hourlyRate, type) {
  const h = Number(hours) || 0;
  const rate = Number(hourlyRate) || 0;
  return h * rate * getOvertimeMultiplier(type);
}

export function isSaudiWeekend(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return false;
  return SAUDI_WEEKEND_DAY_INDICES.includes(d.getDay());
}

export function isSaudiWorkDay(date) {
  return !isSaudiWeekend(date);
}

export function normalizeWorkDay(day) {
  if (!day) return day;
  return LEGACY_FRENCH_DAY_MAP[day] || day;
}

/** Whether the given date falls in Ramadan (Hijri month 9). */
export function isRamadanDate(date) {
  const d = date instanceof Date ? date : new Date(`${String(date).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  try {
    const parts = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', { month: 'numeric' }).formatToParts(d);
    const month = parts.find((p) => p.type === 'month')?.value;
    return month === '9';
  } catch {
    return false;
  }
}

/** Effective standard daily hours (8 h normal, 6 h Ramadan). */
export function getEffectiveDailyHours(date, config = SAUDI_DEFAULT_TIMETRACKING_CONFIG) {
  return isRamadanDate(date) ? config.ramadanDailyHours : config.maxDailyHours;
}

/** Effective weekly hour cap (48 h normal, 36 h Ramadan). */
export function getEffectiveWeeklyHours(date, config = SAUDI_DEFAULT_TIMETRACKING_CONFIG) {
  return isRamadanDate(date) ? config.ramadanWeeklyHours : config.maxWeeklyHours;
}

export function getPointageHijriPref() {
  try {
    const raw = localStorage.getItem(HIJRI_PREF_KEY);
    if (raw) {
      const prefs = JSON.parse(raw);
      return prefs.showHijriDates === true;
    }
    const portalRaw = localStorage.getItem('employee_portal_prefs');
    if (portalRaw) {
      const prefs = JSON.parse(portalRaw);
      return prefs.showHijriDates === true || prefs.useHijriCalendar === '1';
    }
  } catch {
    /* ignore */
  }
  return false;
}

export function setPointageHijriPref(show) {
  try {
    const raw = localStorage.getItem(HIJRI_PREF_KEY);
    const prefs = raw ? JSON.parse(raw) : {};
    localStorage.setItem(HIJRI_PREF_KEY, JSON.stringify({ ...prefs, showHijriDates: !!show }));
  } catch {
    /* ignore */
  }
}

export function formatPointageHijriDate(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  const d = value instanceof Date ? value : new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return fallback;
  try {
    return new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(d);
  } catch {
    return fallback;
  }
}

export function formatPointageDate(value, fallback = '—', options = {}) {
  if (value == null || value === '') return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  const gregorian = d.toLocaleDateString(getBrandLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  });
  const showHijri = options.showHijri ?? getPointageHijriPref();
  if (!showHijri) return gregorian;
  const hijri = formatPointageHijriDate(d, '');
  return hijri ? `${gregorian} (${hijri})` : gregorian;
}

export function formatPointageDateTime(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleString(getBrandLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatPointageTime(value, fallback = '') {
  if (value == null || value === '') return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleTimeString(getBrandLocale(), { hour: '2-digit', minute: '2-digit' });
}

export function formatPointageMonthYear(value) {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(getBrandLocale(), { month: 'long', year: 'numeric' });
}

export function formatPointageShortDate(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString(getBrandLocale(), { day: '2-digit', month: '2-digit' });
}

/** Format monetary amounts for overtime, penalties, bonuses (SAR / ر.س). */
export function formatPointageMoney(amount) {
  return formatBrandMoney(amount);
}

/** Add minutes to HH:MM time string. */
export function addMinutesToTime(timeStr, minutes) {
  if (!timeStr || !/^\d{1,2}:\d{2}$/.test(String(timeStr).trim())) return timeStr;
  const [h, m] = String(timeStr).trim().split(':').map(Number);
  const total = h * 60 + m + (Number(minutes) || 0);
  const nh = Math.floor(total / 60) % 24;
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}

/** Latest acceptable check-in (work start + grace period). */
export function getLateThresholdTime(config = SAUDI_DEFAULT_TIMETRACKING_CONFIG) {
  return addMinutesToTime(config.workDayStart, config.latenessThreshold);
}

/** True when check-in is after the grace period (Saudi default: 08:00 + 15 min). */
export function isLateCheckIn(checkIn, config = SAUDI_DEFAULT_TIMETRACKING_CONFIG) {
  if (!checkIn) return false;
  return String(checkIn).trim() > getLateThresholdTime(config);
}

/** Lateness deduction proportional to hours missed (SAR). */
export function calculateLatenessDeduction(hourlyRate, delayMinutes) {
  const hoursMissed = (Number(delayMinutes) || 0) / 60;
  return hoursMissed * (Number(hourlyRate) || 0);
}

/** Derive hourly rate from monthly salary (salary ÷ 240 — Saudi payroll standard). */
export function calculateHourlyRateFromSalary(monthlySalary) {
  const monthly = Number(monthlySalary) || 0;
  if (monthly <= 0) return 0;
  return monthly / SAUDI_MONTHLY_HOURS_DIVISOR;
}

/** @deprecated Use calculateHourlyRateFromSalary */
export function hourlyRateFromMonthlySalary(monthlySalary) {
  return calculateHourlyRateFromSalary(monthlySalary);
}

export function isSaudiFixedHoliday(date) {
  const d = date instanceof Date ? date : new Date(`${String(date).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const month = d.getMonth() + 1;
  const day = d.getDate();
  return SAUDI_FIXED_HOLIDAYS.some((h) => h.month === month && h.day === day);
}

/** Approximate prayer break slots (actual times vary by city/season). */
export function getSaudiPrayerBreakSlots(workDayStart = '08:00', workDayEnd = '17:00') {
  return SAUDI_PRAYER_NAMES.map((name, index) => ({
    name,
    labelAr: SAUDI_PRAYER_NAMES_AR[name],
    order: index + 1,
    windowStart: workDayStart,
    windowEnd: workDayEnd,
  }));
}

function overtimeEntryYear(entry) {
  const raw = entry?.date;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.getFullYear();
}

function overtimeEntryMonth(entry) {
  const raw = entry?.date;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.getMonth() + 1;
}

function matchesEmployeeOvertime(entry, employeeKey) {
  if (!employeeKey) return true;
  const key = String(employeeKey).toLowerCase();
  const fields = [
    entry?.employee_id,
    entry?.employeeId,
    entry?.employee_name,
    entry?.employeeName,
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());
  return fields.some((f) => f === key || f.includes(key));
}

/** Sum approved/pending overtime hours for a calendar year (max 180 h). */
export function sumOvertimeHoursForYear(overtimes, year, employeeKey = null) {
  if (!Array.isArray(overtimes) || !year) return 0;
  return overtimes
    .filter((o) => {
      if (o?.status === 'rejected') return false;
      if (overtimeEntryYear(o) !== year) return false;
      return matchesEmployeeOvertime(o, employeeKey);
    })
    .reduce((sum, o) => sum + (parseFloat(o.hours) || 0), 0);
}

/** Sum overtime hours for a specific month (1–12). */
export function sumOvertimeHoursForMonth(overtimes, year, month, employeeKey = null) {
  if (!Array.isArray(overtimes) || !year || !month) return 0;
  return overtimes
    .filter((o) => {
      if (o?.status === 'rejected') return false;
      if (overtimeEntryYear(o) !== year) return false;
      if (overtimeEntryMonth(o) !== month) return false;
      return matchesEmployeeOvertime(o, employeeKey);
    })
    .reduce((sum, o) => sum + (parseFloat(o.hours) || 0), 0);
}

/** Absence penalty — up to 5 days' wages (Saudi labor law cap). */
export function calculateAbsencePenalty(dailyWage, penaltyDays = 1, config = SAUDI_DEFAULT_TIMETRACKING_CONFIG) {
  const wage = Number(dailyWage) || 0;
  const days = Math.min(Math.max(Number(penaltyDays) || 1, 0), config.maxAbsencePenaltyDays);
  return days * wage;
}

/** Daily wage from monthly salary (8 h/day standard). */
export function dailyWageFromMonthlySalary(monthlySalary) {
  const hourly = calculateHourlyRateFromSalary(monthlySalary);
  return hourly * SAUDI_DEFAULT_TIMETRACKING_CONFIG.maxDailyHours;
}

/** Parse H:MM or decimal hours to minutes. */
export function parseHoursToMinutes(value) {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value * 60);
  const str = String(value).trim();
  if (/^\d{1,3}:\d{2}$/.test(str)) {
    const [h, m] = str.split(':').map(Number);
    return h * 60 + m;
  }
  const n = parseFloat(str.replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 60) : 0;
}

/**
 * Overtime hours beyond the effective daily standard (8 h or 6 h Ramadan).
 * @param {number} workedMinutes - net working minutes (excluding breaks)
 */
export function calculateOvertimeHours(workedMinutes, date, config = SAUDI_DEFAULT_TIMETRACKING_CONFIG) {
  const standardMinutes = getEffectiveDailyHours(date, config) * 60;
  const extra = (Number(workedMinutes) || 0) - standardMinutes;
  return extra > 0 ? extra / 60 : 0;
}

/** TOIL / recuperation — English API values. */
export const SAUDI_TOIL_RATIO = 1;

export const RECUPERATION_STATUS_VALUES = {
  to_recover: 'to_recover',
  in_progress: 'in_progress',
  recovered: 'recovered',
  expired: 'expired',
};

export const LEGACY_RECUPERATION_STATUS_MAP = {
  'À Récupérer': RECUPERATION_STATUS_VALUES.to_recover,
  'En Cours': RECUPERATION_STATUS_VALUES.in_progress,
  'Récupérée': RECUPERATION_STATUS_VALUES.recovered,
  'Expirée': RECUPERATION_STATUS_VALUES.expired,
};

export const RECUPERATION_REASON_VALUES = {
  overtime: 'overtime',
  weekend: 'weekend',
  holiday: 'holiday',
  other: 'other',
};

export const LEGACY_RECUPERATION_REASON_MAP = {
  'Heures Supplémentaires': RECUPERATION_REASON_VALUES.overtime,
  'Week-end': RECUPERATION_REASON_VALUES.weekend,
  'Jour Férié': RECUPERATION_REASON_VALUES.holiday,
  'Autre': RECUPERATION_REASON_VALUES.other,
};

export function normalizeRecuperationStatus(status) {
  if (!status) return RECUPERATION_STATUS_VALUES.to_recover;
  return LEGACY_RECUPERATION_STATUS_MAP[status] || status;
}

export function normalizeRecuperationReason(reason) {
  if (!reason) return RECUPERATION_REASON_VALUES.overtime;
  return LEGACY_RECUPERATION_REASON_MAP[reason] || reason;
}

/** Last day of acquisition month — TOIL must be taken or paid within same month. */
export function getEndOfMonthDate(value) {
  const d = value instanceof Date ? new Date(value) : new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
}

/** Alias — Gregorian + optional Hijri (ar-SA). */
export function formatPointageDateWithHijri(value, fallback = '—') {
  return formatPointageDate(value, fallback, { showHijri: true });
}

/** Parse "H:MM" or decimal hours to a float. */
export function parseHoursValue(value) {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return value;
  const str = String(value).trim();
  if (str.includes(':')) {
    const [h, m] = str.split(':').map((p) => parseInt(p, 10) || 0);
    return h + m / 60;
  }
  return parseFloat(str) || 0;
}

/** Sum overtime pay (SAR) for a month. */
export function sumOvertimePayForMonth(overtimes, year, month, employeeKey = null) {
  if (!Array.isArray(overtimes) || !year || !month) return 0;
  return overtimes
    .filter((o) => {
      if (o?.status === 'rejected') return false;
      if (overtimeEntryYear(o) !== year) return false;
      if (overtimeEntryMonth(o) !== month) return false;
      return matchesEmployeeOvertime(o, employeeKey);
    })
    .reduce((sum, o) => {
      const stored = parseFloat(o.total_amount ?? o.totalAmount);
      if (!Number.isNaN(stored) && stored > 0) return sum + stored;
      const rate = parseFloat(o.hourly_rate ?? o.hourlyRate) || 0;
      return sum + calculateOvertimeAmount(o.hours, rate, o.type);
    }, 0);
}

const ABSENCE_CODES = new Set(['AN', 'AJ']);
const LATE_CODES = new Set(['R']);
const LEAVE_CODES = new Set(['CM', 'CRP', 'CSS', 'CP', 'CS']);

function roundHours(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Monthly hours summary per employee (Saudi 48 h/week standard, overtime in SAR). */
export function calculateEmployeeMonthlyHoursSummary({
  entries = [],
  employeeKey,
  year,
  month,
  overtimes = [],
  monthlySalary = 0,
}) {
  const monthStr = String(month).padStart(2, '0');
  const prefix = `${year}-${monthStr}-`;
  const empEntries = entries.filter((e) => {
    const date = String(e.date || '').slice(0, 10);
    if (!date.startsWith(prefix)) return false;
    const eid = String(e.employeeId ?? e.employee_id ?? '');
    if (!employeeKey) return true;
    return eid === String(employeeKey);
  });

  let totalWorked = 0;
  let absenceDays = 0;
  let lateDays = 0;
  let leaveDays = 0;

  empEntries.forEach((entry) => {
    const code = entry.status || entry.code || '';
    const dailyHours = parseHoursValue(entry.totalHours);
    if (ABSENCE_CODES.has(code)) {
      absenceDays += 1;
      return;
    }
    if (LATE_CODES.has(code)) lateDays += 1;
    if (LEAVE_CODES.has(code)) {
      leaveDays += 1;
      return;
    }
    if (code === 'P' || code === 'P/2' || dailyHours > 0) {
      totalWorked += dailyHours > 0 ? dailyHours : (code === 'P/2' ? 4 : SAUDI_STANDARD_DAILY_HOURS);
    }
  });

  const overtimeHours = sumOvertimeHoursForMonth(overtimes, year, month, employeeKey);
  const hourlyRate = calculateHourlyRateFromSalary(monthlySalary);
  const overtimePay = sumOvertimePayForMonth(overtimes, year, month, employeeKey)
    || calculateOvertimeAmount(overtimeHours, hourlyRate, OVERTIME_TYPE_VALUES.regular);
  const regularHours = Math.max(0, totalWorked - overtimeHours);

  return {
    totalWorked: roundHours(totalWorked),
    regularHours: roundHours(regularHours),
    overtimeHours: roundHours(overtimeHours),
    overtimePay,
    absenceHours: roundHours(absenceDays * SAUDI_STANDARD_DAILY_HOURS),
    lateHours: roundHours(lateDays),
    leaveHours: roundHours(leaveDays * SAUDI_STANDARD_DAILY_HOURS),
    netHoursWorked: roundHours(totalWorked),
    hourlyRate,
    yearlyOvertimeHours: sumOvertimeHoursForYear(overtimes, year, employeeKey),
  };
}

/** Compare two HH:MM strings. */
export function compareTimeStrings(a, b) {
  if (!a || !b) return 0;
  return String(a).localeCompare(String(b));
}

export const DAY_LABEL_KEYS = {
  Sunday: 'day_sunday',
  Monday: 'day_monday',
  Tuesday: 'day_tuesday',
  Wednesday: 'day_wednesday',
  Thursday: 'day_thursday',
  Friday: 'day_friday',
  Saturday: 'day_saturday',
  Lundi: 'day_monday',
  Mardi: 'day_tuesday',
  Mercredi: 'day_wednesday',
  Jeudi: 'day_thursday',
  Vendredi: 'day_friday',
  Samedi: 'day_saturday',
  Dimanche: 'day_sunday',
};

export const SHIFT_TYPE_LABEL_KEYS = {
  [SHIFT_TYPE_VALUES.day]: 'shift_day',
  [SHIFT_TYPE_VALUES.morning]: 'shift_morning',
  [SHIFT_TYPE_VALUES.evening]: 'shift_evening',
  [SHIFT_TYPE_VALUES.night]: 'shift_night',
  [SHIFT_TYPE_VALUES.rotating]: 'shift_rotating',
  [SHIFT_TYPE_VALUES.ramadan]: 'shift_ramadan',
  [SHIFT_TYPE_VALUES.weekend]: 'shift_weekend',
  Matin: 'shift_morning',
  'Après-midi': 'shift_evening',
  Nuit: 'shift_night',
  'Week-end': 'shift_weekend',
  Flexible: 'shift_day',
};

export const LEAVE_TYPE_LABEL_KEYS = {
  [LEAVE_TYPE_VALUES.annual]: 'leave_annual',
  [LEAVE_TYPE_VALUES.sick]: 'leave_sick',
  [LEAVE_TYPE_VALUES.unpaid]: 'leave_unpaid',
  [LEAVE_TYPE_VALUES.maternity]: 'leave_maternity',
  [LEAVE_TYPE_VALUES.hajj]: 'leave_hajj',
  [LEAVE_TYPE_VALUES.bereavement]: 'leave_bereavement',
  [LEAVE_TYPE_VALUES.marriage]: 'leave_marriage',
  [LEAVE_TYPE_VALUES.paternity]: 'leave_paternity',
  'Congé Annuel': 'leave_annual',
  'Congé Maladie': 'leave_sick',
  'Congé Sans Solde': 'leave_unpaid',
  'Congé Maternité': 'leave_maternity',
  RTT: 'leave_unpaid',
};

export const HOLIDAY_TYPE_LABEL_KEYS = {
  [HOLIDAY_TYPE_VALUES.national]: 'holiday_type_national',
  [HOLIDAY_TYPE_VALUES.religious]: 'holiday_type_religious',
  [HOLIDAY_TYPE_VALUES.company]: 'holiday_type_company',
  [HOLIDAY_TYPE_VALUES.regional]: 'holiday_type_regional',
  Religieux: 'holiday_type_religious',
  Entreprise: 'holiday_type_company',
  Régional: 'holiday_type_regional',
};

export const HOLIDAY_CATEGORY_LABEL_KEYS = {
  [HOLIDAY_CATEGORY_VALUES.paid]: 'holiday_category_paid',
  [HOLIDAY_CATEGORY_VALUES.unpaid]: 'holiday_category_unpaid',
  [HOLIDAY_CATEGORY_VALUES.bridge]: 'holiday_category_bridge',
  'Férié Payé': 'holiday_category_paid',
  'Férié Non Payé': 'holiday_category_unpaid',
  Pont: 'holiday_category_bridge',
};

export const ZONE_TYPE_LABEL_KEYS = {
  [ZONE_TYPE_VALUES.office]: 'zone_office',
  [ZONE_TYPE_VALUES.factory]: 'zone_factory',
  [ZONE_TYPE_VALUES.warehouse]: 'zone_warehouse',
  [ZONE_TYPE_VALUES.site]: 'zone_site',
  [ZONE_TYPE_VALUES.branch]: 'zone_branch',
  Bureau: 'zone_office',
  Usine: 'zone_factory',
  Entrepôt: 'zone_warehouse',
  Chantier: 'zone_site',
  Agence: 'zone_branch',
};

export const PROFILE_TYPE_LABEL_KEYS = {
  [PROFILE_TYPE_VALUES.standard]: 'profile_standard',
  [PROFILE_TYPE_VALUES.custom]: 'profile_custom',
  [PROFILE_TYPE_VALUES.fullTime]: 'profile_full_time',
  [PROFILE_TYPE_VALUES.partTime]: 'profile_part_time',
  [PROFILE_TYPE_VALUES.executive]: 'profile_executive',
  Personnalisé: 'profile_custom',
  'Temps Plein': 'profile_full_time',
  'Temps Partiel': 'profile_part_time',
  Cadre: 'profile_executive',
};

export const FLEXIBILITY_TYPE_LABEL_KEYS = {
  [FLEXIBILITY_TYPE_VALUES.fixed]: 'flexibility_fixed',
  [FLEXIBILITY_TYPE_VALUES.variable]: 'flexibility_variable',
  [FLEXIBILITY_TYPE_VALUES.remote]: 'flexibility_remote',
  [FLEXIBILITY_TYPE_VALUES.hybrid]: 'flexibility_hybrid',
  Fixe: 'flexibility_fixed',
  'Horaire Variable': 'flexibility_variable',
  Télétravail: 'flexibility_remote',
  Hybride: 'flexibility_hybrid',
};

export const ASSIGNMENT_STATUS_LABEL_KEYS = {
  [ASSIGNMENT_STATUS_VALUES.active]: 'assignment_active',
  [ASSIGNMENT_STATUS_VALUES.suspended]: 'assignment_suspended',
  [ASSIGNMENT_STATUS_VALUES.ended]: 'assignment_ended',
  [ASSIGNMENT_STATUS_VALUES.planned]: 'assignment_planned',
  Suspendue: 'assignment_suspended',
  Terminée: 'assignment_ended',
  Planifiée: 'assignment_planned',
};

export const ASSIGNMENT_TYPE_LABEL_KEYS = {
  [ASSIGNMENT_TYPE_VALUES.permanent]: 'assignment_permanent',
  [ASSIGNMENT_TYPE_VALUES.temporary]: 'assignment_temporary',
  [ASSIGNMENT_TYPE_VALUES.mission]: 'assignment_mission',
  Permanente: 'assignment_permanent',
  Temporaire: 'assignment_temporary',
  Mission: 'assignment_mission',
};

export const TEAM_ROLE_LABEL_KEYS = {
  [TEAM_ROLE_VALUES.teamLead]: 'team_lead',
  [TEAM_ROLE_VALUES.member]: 'team_member',
  [TEAM_ROLE_VALUES.substitute]: 'team_substitute',
  [TEAM_ROLE_VALUES.intern]: 'team_intern',
  "Chef d'Équipe": 'team_lead',
  Membre: 'team_member',
  Remplaçant: 'team_substitute',
  Stagiaire: 'team_intern',
};

export const JUSTIFICATION_TYPE_LABEL_KEYS = {
  [JUSTIFICATION_TYPE_VALUES.medical]: 'justification_medical',
  [JUSTIFICATION_TYPE_VALUES.death]: 'justification_death',
  [JUSTIFICATION_TYPE_VALUES.marriage]: 'justification_marriage',
  [JUSTIFICATION_TYPE_VALUES.emergency]: 'justification_emergency',
  [JUSTIFICATION_TYPE_VALUES.hajjTravel]: 'justification_hajj_travel',
  [JUSTIFICATION_TYPE_VALUES.court]: 'justification_court',
  [JUSTIFICATION_TYPE_VALUES.late]: 'justification_late',
  [JUSTIFICATION_TYPE_VALUES.absence]: 'justification_absence',
  [JUSTIFICATION_TYPE_VALUES.other]: 'justification_other',
  'Certificat Médical': 'justification_medical',
  Attestation: 'justification_emergency',
  'Justificatif Retard': 'justification_late',
  'Autorisation Absence': 'justification_absence',
  Autre: 'justification_other',
};

export const JUSTIFICATION_STATUS_LABEL_KEYS = {
  [JUSTIFICATION_STATUS_VALUES.pending]: 'justification_pending',
  [JUSTIFICATION_STATUS_VALUES.validated]: 'justification_validated',
  [JUSTIFICATION_STATUS_VALUES.rejected]: 'justification_rejected',
  'En Attente': 'justification_pending',
  Validé: 'justification_validated',
  Rejeté: 'justification_rejected',
};

export const LEAVE_STATUS_LABEL_KEYS = {
  [LEAVE_STATUS_VALUES.pending]: 'leave_pending',
  [LEAVE_STATUS_VALUES.approved]: 'leave_approved',
  [LEAVE_STATUS_VALUES.rejected]: 'leave_rejected',
  'En Attente': 'leave_pending',
  Approuvée: 'leave_approved',
  Rejetée: 'leave_rejected',
};

export const OVERTIME_TYPE_LABEL_KEYS = {
  [OVERTIME_TYPE_VALUES.regular]: 'overtime_regular',
  [OVERTIME_TYPE_VALUES.friday]: 'overtime_friday',
  [OVERTIME_TYPE_VALUES.holiday]: 'overtime_holiday',
  [OVERTIME_TYPE_VALUES.night]: 'overtime_night',
  Normal: 'overtime_regular',
  Nuit: 'overtime_night',
  Weekend: 'overtime_friday',
  'Férié': 'overtime_holiday',
  Ferie: 'overtime_holiday',
};

export const RECUPERATION_STATUS_LABEL_KEYS = {
  [RECUPERATION_STATUS_VALUES.to_recover]: 'recuperation_to_recover',
  [RECUPERATION_STATUS_VALUES.in_progress]: 'recuperation_in_progress',
  [RECUPERATION_STATUS_VALUES.recovered]: 'recuperation_recovered',
  [RECUPERATION_STATUS_VALUES.expired]: 'recuperation_expired',
  'À Récupérer': 'recuperation_to_recover',
  'En Cours': 'recuperation_in_progress',
  'Récupérée': 'recuperation_recovered',
  'Expirée': 'recuperation_expired',
};

export const RECUPERATION_REASON_LABEL_KEYS = {
  [RECUPERATION_REASON_VALUES.overtime]: 'recuperation_reason_overtime',
  [RECUPERATION_REASON_VALUES.weekend]: 'recuperation_reason_weekend',
  [RECUPERATION_REASON_VALUES.holiday]: 'recuperation_reason_holiday',
  [RECUPERATION_REASON_VALUES.other]: 'recuperation_reason_other',
  'Heures Supplémentaires': 'recuperation_reason_overtime',
  'Week-end': 'recuperation_reason_weekend',
  'Jour Férié': 'recuperation_reason_holiday',
  Autre: 'recuperation_reason_other',
};

export function translatePointageLabel(value, labelKeyMap, t, normalizeFn = null, fallback = '—') {
  if (value == null || value === '') return fallback;
  const normalized = normalizeFn ? normalizeFn(value) : value;
  const key = labelKeyMap[normalized] || labelKeyMap[value];
  return key ? t(`${POINTAGE_HELPERS_I18N_NS}.${key}`) : (normalized || fallback);
}

export const getPointageDayLabel = (day, t) => translatePointageLabel(day, DAY_LABEL_KEYS, t, normalizeWorkDay);
export const getPointageShiftTypeLabel = (type, t) => translatePointageLabel(type, SHIFT_TYPE_LABEL_KEYS, t, normalizeShiftType);
export const getPointageLeaveTypeLabel = (type, t) => translatePointageLabel(type, LEAVE_TYPE_LABEL_KEYS, t, normalizeLeaveType);
export const getPointageHolidayTypeLabel = (type, t) => translatePointageLabel(type, HOLIDAY_TYPE_LABEL_KEYS, t, normalizeHolidayType);
export const getPointageHolidayCategoryLabel = (category, t) => translatePointageLabel(category, HOLIDAY_CATEGORY_LABEL_KEYS, t, normalizeHolidayCategory);
export const getPointageZoneTypeLabel = (type, t) => translatePointageLabel(type, ZONE_TYPE_LABEL_KEYS, t, normalizeZoneType);
export const getPointageProfileTypeLabel = (type, t) => translatePointageLabel(type, PROFILE_TYPE_LABEL_KEYS, t, normalizeProfileType);
export const getPointageFlexibilityTypeLabel = (type, t) => translatePointageLabel(type, FLEXIBILITY_TYPE_LABEL_KEYS, t, normalizeFlexibilityType);
export const getPointageAssignmentStatusLabel = (status, t) => translatePointageLabel(status, ASSIGNMENT_STATUS_LABEL_KEYS, t, normalizeAssignmentStatus);
export const getPointageAssignmentTypeLabel = (type, t) => translatePointageLabel(type, ASSIGNMENT_TYPE_LABEL_KEYS, t, normalizeAssignmentType);
export const getPointageTeamRoleLabel = (role, t) => translatePointageLabel(role, TEAM_ROLE_LABEL_KEYS, t, normalizeTeamRole);
export const getPointageJustificationTypeLabel = (type, t) => translatePointageLabel(type, JUSTIFICATION_TYPE_LABEL_KEYS, t, normalizeJustificationType);
export const getPointageJustificationStatusLabel = (status, t) => translatePointageLabel(status, JUSTIFICATION_STATUS_LABEL_KEYS, t, normalizeJustificationStatus);
export const getPointageLeaveStatusLabel = (status, t) => translatePointageLabel(status, LEAVE_STATUS_LABEL_KEYS, t, normalizeLeaveStatus);
export const getPointageOvertimeTypeLabel = (type, t) => translatePointageLabel(type, OVERTIME_TYPE_LABEL_KEYS, t, normalizeOvertimeType);
export const getPointageRecuperationStatusLabel = (status, t) => translatePointageLabel(status, RECUPERATION_STATUS_LABEL_KEYS, t, normalizeRecuperationStatus);
export const getPointageRecuperationReasonLabel = (reason, t) => translatePointageLabel(reason, RECUPERATION_REASON_LABEL_KEYS, t, normalizeRecuperationReason);
